import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  Paper,
  Grid,
  CircularProgress,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DeleteIcon from '@mui/icons-material/Delete';
import api from '../../services/api';
import ubicacionService from '../../services/ubicacionService';
import type {
  NotaCreditoCreateDto,
  NotaCreditoFormularioCreateDto,
  NotaCreditoItemCreateDto,
} from '../../services/notaCreditoVentaService';
import { formatCurrency, normalizeItemNotas } from '../../utils/formatters';
import VendedorSearch from '../../components/VendedorSearch';
import vendedorService, { Vendedor } from '../../services/vendedorService';

interface Compania {
  idCompania: string;
  nombre: string;
}

interface SucursalVenta {
  nroSuc: number;
  nombreSucursal?: string;
}

interface UbicacionItem {
  nroUbicacion: number;
  nombreUbicacion?: string;
}

const NOTA_CREDITO_ITEM_NOTAS_MAX = 500;

const FORMAS_DE_PAGO = [
  'CONTADO',
  'CTA.CTE.',
  'EFECTIVO',
  'notaCredito',
  'CUOTAS',
  'CONTRAREEMBO',
  'PAGOANTICIPA',
];

export interface NotaCreditoFormState {
  formulario: NotaCreditoFormularioCreateDto;
  items: NotaCreditoItemCreateDto[];
}

interface NotaCreditoFormProps {
  initialState: NotaCreditoFormState;
  onSave: (payload: NotaCreditoCreateDto) => void;
  loading?: boolean;
}

const NotaCreditoForm: React.FC<NotaCreditoFormProps> = ({
  initialState,
  onSave,
  loading = false,
}) => {
  const [formulario, setFormulario] = useState<NotaCreditoFormularioCreateDto>(initialState.formulario);
  const [items, setItems] = useState<NotaCreditoItemCreateDto[]>(initialState.items);
  const [companias, setCompanias] = useState<Compania[]>([]);
  const [sucursales, setSucursales] = useState<SucursalVenta[]>([]);
  const [ubicaciones, setUbicaciones] = useState<UbicacionItem[]>([]);
  const [openVendedorSearch, setOpenVendedorSearch] = useState(false);
  const [vendedorSeleccionado, setVendedorSeleccionado] = useState<Pick<Vendedor, 'nroVendedor' | 'nombreVendedor'> | null>(null);

  useEffect(() => {
    const loadCompanias = async () => {
      try {
        const res = await api.get('/api/Compañias');
        const data = res.data;
        let list: Compania[] = Array.isArray(data) ? data : Array.isArray((data as any)?.items) ? (data as any).items : [];
        setCompanias(list.map((c: any) => ({ idCompania: String(c.idCompania || '').trim(), nombre: String(c.nombre || '').trim() })));
      } catch {
        setCompanias([]);
      }
    };
    const loadSucursales = async () => {
      try {
        const res = await api.get('/api/SucursalesVenta');
        const data = res.data;
        const list = data?.items ?? (Array.isArray(data) ? data : []);
        setSucursales(list);
      } catch {
        setSucursales([]);
      }
    };
    const loadUbicaciones = async () => {
      try {
        const list = await ubicacionService.getUbicaciones();
        setUbicaciones(Array.isArray(list) ? list : []);
      } catch {
        setUbicaciones([]);
      }
    };
    loadCompanias();
    loadSucursales();
    loadUbicaciones();
  }, []);

  useEffect(() => {
    const n = Number(formulario.nroVendedor);
    if (!Number.isFinite(n) || n <= 0) {
      setVendedorSeleccionado(null);
      return;
    }
    let cancelled = false;
    vendedorService
      .getVendedor(n)
      .then((v) => {
        if (!cancelled && v) {
          setVendedorSeleccionado({ nroVendedor: v.nroVendedor, nombreVendedor: v.nombreVendedor });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setVendedorSeleccionado({ nroVendedor: n, nombreVendedor: '' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [formulario.nroVendedor]);

  useEffect(() => {
    if (companias.length > 0 && !formulario.idCompania) {
      const first = companias[0];
      if (first?.idCompania) {
        setFormulario((f) => ({ ...f, idCompania: first.idCompania }));
      }
    }
  }, [companias, formulario.idCompania]);

  const handleFormularioChange = (updates: Partial<NotaCreditoFormularioCreateDto>) => {
    setFormulario((f) => ({ ...f, ...updates }));
  };

  const handleSelectVendedor = (v: Vendedor) => {
    setVendedorSeleccionado({ nroVendedor: v.nroVendedor, nombreVendedor: v.nombreVendedor });
    handleFormularioChange({ nroVendedor: v.nroVendedor });
    setOpenVendedorSearch(false);
  };

  const updateItem = (index: number, updates: Partial<NotaCreditoItemCreateDto>) => {
    setItems((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      return next;
    });
  };

  const removeItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const subtotalItem = (item: NotaCreditoItemCreateDto) => {
    const qty = Number(item.cantidad) || 0;
    const price = Number(item.precioUnitario) || 0;
    return Math.round(qty * price * 100) / 100;
  };

  const totalGeneral = items.reduce((sum, it) => sum + subtotalItem(it), 0);

  const handleSubmit = () => {
    if (items.length === 0) return;
    const payload: NotaCreditoCreateDto = {
      formulario: { ...formulario },
      items: items.map((it) => ({
        codigoArticulo: String(it.codigoArticulo ?? '').trim(),
        descripcion: it.descripcion?.trim(),
        cantidad: Number(it.cantidad) || 0,
        precioUnitario: Number(it.precioUnitario) || 0,
        um: it.um?.trim(),
        codigoMoneda: it.codigoMoneda,
        cotizacion: it.cotizacion,
        notas: (() => {
          const n = normalizeItemNotas(it.notas, NOTA_CREDITO_ITEM_NOTAS_MAX);
          return n || undefined;
        })(),
      })),
    };
    onSave(payload);
  };

  const fechaEmisionStr = formulario.fechaEmision
    ? (typeof formulario.fechaEmision === 'string'
        ? formulario.fechaEmision
        : (formulario.fechaEmision as Date).toISOString?.())
    : '';
  const fechaEmisionDate = fechaEmisionStr ? fechaEmisionStr.slice(0, 10) : '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Card sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Encabezado
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Factura origen"
              value={formulario.nroFormularioFactura ?? ''}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Cliente"
              value={`${formulario.nroCliente}${formulario.nombre ? ` - ${formulario.nombre}` : ''}`}
              InputProps={{ readOnly: true }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Nº Vendedor"
              name="nroVendedor"
              value={
                vendedorSeleccionado
                  ? `${vendedorSeleccionado.nroVendedor} - ${vendedorSeleccionado.nombreVendedor?.trim() || ''}`
                  : formulario.nroVendedor != null && formulario.nroVendedor > 0
                    ? String(formulario.nroVendedor)
                    : ''
              }
              onChange={(e) => {
                const numPart = e.target.value.trim().split(/\s*-\s*/)[0].trim();
                const num = parseInt(numPart, 10);
                handleFormularioChange({
                  nroVendedor: Number.isFinite(num) && num > 0 ? num : undefined,
                });
                if (!numPart || !Number.isFinite(num) || num <= 0) {
                  setVendedorSeleccionado(null);
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setOpenVendedorSearch(true)}
                      edge="end"
                      color="primary"
                      title="Buscar vendedor"
                      size="small"
                    >
                      <ArrowDropDownIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Compañía</InputLabel>
              <Select
                label="Compañía"
                value={formulario.idCompania || ''}
                onChange={(e: SelectChangeEvent<string>) => handleFormularioChange({ idCompania: e.target.value || '' })}
              >
                {companias.map((c) => (
                  <MenuItem key={c.idCompania} value={c.idCompania}>
                    {c.idCompania} - {c.nombre}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Nº Sucursal</InputLabel>
              <Select
                label="Nº Sucursal"
                value={formulario.nroSuc ?? ''}
                onChange={(e) => handleFormularioChange({ nroSuc: Number(e.target.value) || undefined })}
              >
                {sucursales.map((s) => (
                  <MenuItem key={s.nroSuc} value={s.nroSuc}>
                    {s.nroSuc} - {s.nombreSucursal || `Sucursal ${s.nroSuc}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth required>
              <InputLabel>Ubicación origen</InputLabel>
              <Select
                label="Ubicación origen"
                value={formulario.nroUbicacionOrigen ?? ''}
                onChange={(e) => handleFormularioChange({ nroUbicacionOrigen: Number(e.target.value) || undefined })}
              >
                {ubicaciones.map((u) => (
                  <MenuItem key={u.nroUbicacion} value={u.nroUbicacion}>
                    {u.nombreUbicacion || `Ubicación ${u.nroUbicacion}`} ({u.nroUbicacion})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Fecha emisión"
              type="date"
              value={fechaEmisionDate}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => {
                const v = e.target.value;
                if (v) {
                  handleFormularioChange({ fechaEmision: new Date(v).toISOString(), fechaVencim: new Date(v).toISOString() });
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Forma de pago</InputLabel>
              <Select
                label="Forma de pago"
                value={formulario.formaDePago || ''}
                onChange={(e) => handleFormularioChange({ formaDePago: e.target.value || undefined })}
              >
                {FORMAS_DE_PAGO.map((f) => (
                  <MenuItem key={f} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Descripción / Motivo"
              value={formulario.descripcion ?? ''}
              onChange={(e) => handleFormularioChange({ descripcion: e.target.value || undefined })}
            />
          </Grid>
        </Grid>
      </Card>

      <Card sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Detalle
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Artículo</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell align="right">Cantidad</TableCell>
                <TableCell align="right">P. unit.</TableCell>
                <TableCell>Notas</TableCell>
                <TableCell align="right">Subtotal</TableCell>
                <TableCell width={50} />
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell>{item.codigoArticulo}</TableCell>
                  <TableCell>{item.descripcion || '-'}</TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      inputProps={{ min: 0.01, step: 0.01 }}
                      value={item.cantidad}
                      onChange={(e) => updateItem(idx, { cantidad: Number(e.target.value) || 0 })}
                      sx={{ width: 90 }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      type="number"
                      size="small"
                      inputProps={{ min: 0, step: 0.01 }}
                      value={item.precioUnitario}
                      onChange={(e) => updateItem(idx, { precioUnitario: Number(e.target.value) || 0 })}
                      sx={{ width: 100 }}
                    />
                  </TableCell>
                  <TableCell sx={{ minWidth: 200, maxWidth: 280 }}>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Notas"
                      value={item.notas ?? ''}
                      onChange={(e) =>
                        updateItem(idx, {
                          notas: e.target.value.slice(0, NOTA_CREDITO_ITEM_NOTAS_MAX),
                        })
                      }
                      multiline
                      minRows={1}
                      inputProps={{ maxLength: NOTA_CREDITO_ITEM_NOTAS_MAX }}
                      helperText={`${(item.notas ?? '').length}/${NOTA_CREDITO_ITEM_NOTAS_MAX}`}
                    />
                  </TableCell>
                  <TableCell align="right">{formatCurrency(subtotalItem(item))}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => removeItem(idx)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box display="flex" gap={3} alignItems="center">
            <Typography variant="body2">Artículos ({items.length})</Typography>
            <Typography variant="body2">Cantidad total: {items.reduce((sum, it) => sum + (Number(it.cantidad) || 0), 0)}</Typography>
          </Box>
          <Typography variant="h6">
            Total: {formatCurrency(totalGeneral)}
          </Typography>
        </Box>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading || items.length === 0 || !formulario.idCompania || !formulario.nroUbicacionOrigen}
        >
          {loading ? <CircularProgress size={24} /> : 'Guardar Nota de Crédito'}
        </Button>
      </Box>

      <VendedorSearch
        open={openVendedorSearch}
        onClose={() => setOpenVendedorSearch(false)}
        onSelect={handleSelectVendedor}
      />
    </Box>
  );
};

export default NotaCreditoForm;
